import React, { useState, useEffect } from 'react';
import { createUnregisteredTourReservation, getOrderSaleStatus } from '../services/apiService';
import { useNavigate } from 'react-router-dom';

const phoneReg = /^\d{2,3}-?\d{3,4}-?\d{4}$/;

const NewReservationModal = ({ open, onClose }) => {
  const navigate = useNavigate();
  const [mounted, setMounted] = useState(false);
  const [animatingOut, setAnimatingOut] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    productName: '',
    durationDays: '',
    startDate: '',
    reserverName: '',
    phone: '',
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open) {
      setAnimatingOut(false);
      const id = requestAnimationFrame(() => setMounted(true));
      return () => cancelAnimationFrame(id);
    }
  }, [open]);

  const close = () => {
    setAnimatingOut(true);
    setMounted(false);
    window.setTimeout(() => onClose && onClose(), 220);
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const validate = () => {
    const e = {};
    if (!form.productName.trim()) e.productName = '상품명을 입력하세요.';
    if (!String(form.durationDays).trim() || !/^\d+$/.test(String(form.durationDays))) e.durationDays = '소요일수(일)을 숫자로 입력하세요.';
    if (!form.startDate) e.startDate = '행사시작일을 선택하세요 (YYYY-MM-DD).';
    if (!form.reserverName.trim()) e.reserverName = '예약자를 입력하세요.';
    if (!form.phone.trim() || !phoneReg.test(form.phone.replace(/-/g, ''))) e.phone = '유효한 연락처를 입력하세요.';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (e) => {
    e && e.preventDefault();
    if (!validate()) return;
    try {
      setLoading(true);
      const res = await createUnregisteredTourReservation({
        productName: form.productName,
        durationDays: Number(form.durationDays),
        startDate: form.startDate,
        reserverName: form.reserverName,
        phone: form.phone,
      });
      // Expecting { table: [{ 주문번호: number }] }
      const row = Array.isArray(res?.table) ? res.table[0] : Array.isArray(res?.Table) ? res.Table[0] : null;
      const orderNo = row?.주문번호 ?? row?.orderNo ?? row?.OrderNo;
      let saleNo = row?.판매번호 ?? row?.saleNo ?? row?.주문판매번호;
      if (!orderNo) {
        alert('주문번호를 받지 못했습니다. 관리자에게 문의해주세요.');
        return;
      }
      try {
        if (!saleNo && orderNo) {
          const statusRow = await getOrderSaleStatus({ orderNo });
          saleNo = statusRow?.판매번호 ?? statusRow?.주문판매번호 ?? null;
        }
      } catch {}
      navigate(`/reservation-detail/${String(orderNo)}`, { state: { saleNo, productName: form.productName } });
      close();
    } catch (err) {
      console.error('신규예약 생성 실패', err);
      alert(err?.message || '신규예약 생성에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div
        className={`bg-white rounded-lg shadow-xl w-full max-w-lg overflow-hidden transform transition-all ${mounted ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}
        style={{ maxHeight: '90vh' }}
      >
        {/* Header */}
        <div className="px-6 py-4 bg-primary-600 text-white flex justify-between items-center">
          <h3 className="text-lg font-semibold">신규예약 추가</h3>
          <button type="button" className="text-white hover:text-gray-200" onClick={close}>
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 130px)' }}>
          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">상품명</label>
              <input
                type="text"
                name="productName"
                value={form.productName}
                onChange={onChange}
                className={`w-full px-3 py-2 border ${errors.productName ? 'border-red-500' : 'border-gray-300'} rounded-md`}
                placeholder="상품명을 입력하세요"
              />
              {errors.productName && <p className="mt-1 text-sm text-red-600">{errors.productName}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">소요일수 (일)</label>
              <input
                type="number"
                name="durationDays"
                value={form.durationDays}
                onChange={onChange}
                className={`w-full px-3 py-2 border ${errors.durationDays ? 'border-red-500' : 'border-gray-300'} rounded-md`}
                min={0}
                placeholder="예: 5"
              />
              {errors.durationDays && <p className="mt-1 text-sm text-red-600">{errors.durationDays}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">행사시작일</label>
              <input
                type="date"
                name="startDate"
                value={form.startDate}
                onChange={onChange}
                className={`w-full px-3 py-2 border ${errors.startDate ? 'border-red-500' : 'border-gray-300'} rounded-md`}
              />
              {errors.startDate && <p className="mt-1 text-sm text-red-600">{errors.startDate}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">예약자</label>
              <input
                type="text"
                name="reserverName"
                value={form.reserverName}
                onChange={onChange}
                className={`w-full px-3 py-2 border ${errors.reserverName ? 'border-red-500' : 'border-gray-300'} rounded-md`}
                placeholder="예약자 이름"
              />
              {errors.reserverName && <p className="mt-1 text-sm text-red-600">{errors.reserverName}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">연락처</label>
              <input
                type="tel"
                name="phone"
                value={form.phone}
                onChange={onChange}
                className={`w-full px-3 py-2 border ${errors.phone ? 'border-red-500' : 'border-gray-300'} rounded-md`}
                placeholder="010-0000-0000"
              />
              {errors.phone && <p className="mt-1 text-sm text-red-600">{errors.phone}</p>}
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end">
          <button type="button" className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md mr-2" onClick={close}>
            취소
          </button>
          <button type="button" className="px-4 py-2 bg-primary-600 text-white rounded-md min-w-[100px]" onClick={onSubmit} disabled={loading}>
            {loading ? '처리중...' : '신규예약 생성'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NewReservationModal;
